/**
 * =============================================================================
 * FETCH WITH RETRY UTILITY (ADMIN UI)
 * =============================================================================
 *
 * Utility function for making fetch requests with automatic retry
 * and exponential backoff on transient failures.
 *
 * PHASE 1 TASK: P1-12 - Add Retry Logic with Exponential Backoff
 * PATTERN REFERENCE: Based on storefront-widget/fetchWithRetry.ts
 *
 * Features:
 * - Exponential backoff (1s, 2s, 4s by default)
 * - Configurable max retries (default: 3)
 * - Only retries on network errors and 5xx server errors
 * - Does not retry 4xx client errors (permanent failures)
 * - Integrates with existing timeout handling (P1-11)
 *
 * @example
 * ```typescript
 * // Basic usage
 * const response = await fetchWithRetry('/api/admin/questions', {
 *   headers: { Authorization: `Bearer ${token}` }
 * });
 *
 * // With custom options
 * const response = await fetchWithRetry('/api/data', {
 *   method: 'POST',
 *   body: JSON.stringify(data),
 * }, { maxRetries: 5, baseDelayMs: 500 });
 * ```
 *
 * =============================================================================
 */

// Source the timeout constant from `./constants`, NOT `./client`. The
// previous `import … from './client'` formed a circular dependency
// (client → fetchWithRetry → client) that crashed module init in the
// browser bundle with "Cannot access 'DEFAULT_REQUEST_TIMEOUT_MS' before
// initialization", blanking the admin UI before the post-install screen or
// the authenticated dashboard could render. See ./constants.ts for the
// rationale comment.
import { DEFAULT_REQUEST_TIMEOUT_MS } from './constants';

/**
 * Configuration options for fetchWithRetry.
 */
export interface RetryOptions {
  /** Maximum number of retry attempts. Default: 3 */
  maxRetries?: number;
  /** Base delay in milliseconds for exponential backoff. Default: 1000 */
  baseDelayMs?: number;
  /** HTTP status codes that should trigger a retry. Default: 5xx errors */
  retryableStatuses?: number[];
  /** Request timeout in milliseconds. Default: 30000 (30s). */
  timeoutMs?: number;
  /**
   * Return the final retryable HTTP response instead of replacing its JSON
   * body with `MaxRetriesExceededError`. This is intentionally opt-in: guarded
   * mutations use it with `maxRetries: 0` so a known partial-application report
   * survives, while existing retrying callers keep the legacy envelope.
   */
  preserveFinalRetryableResponse?: boolean;
}

/**
 * Error thrown when max retries are exceeded.
 */
export class MaxRetriesExceededError extends Error {
  public readonly attempts: number;
  public readonly lastError: unknown;

  constructor(attempts: number, lastError: unknown) {
    const message =
      lastError instanceof Error
        ? `Request failed after ${attempts} attempts: ${lastError.message}`
        : `Request failed after ${attempts} attempts`;
    super(message);
    this.name = 'MaxRetriesExceededError';
    this.attempts = attempts;
    this.lastError = lastError;
  }
}

/**
 * Error thrown when a request times out.
 */
export class RequestTimeoutError extends Error {
  constructor() {
    super('The request timed out. Please check your connection and try again.');
    this.name = 'RequestTimeoutError';
  }
}

/**
 * Default retry options.
 */
const DEFAULT_OPTIONS: Required<RetryOptions> = {
  maxRetries: 3,
  baseDelayMs: 1000,
  // Retry on 5xx server errors - client shouldn't retry on 4xx
  retryableStatuses: [500, 502, 503, 504],
  timeoutMs: DEFAULT_REQUEST_TIMEOUT_MS,
  preserveFinalRetryableResponse: false,
};

/**
 * Combine multiple abort signals into one.
 * Uses native AbortSignal.any when available, falls back to polyfill.
 *
 * Review fix: MULTI-TASK-REVIEW-POST-P1-23
 * - Consumer-provided signal was being overwritten by timeout signal
 * - This helper combines both signals so consumers can cancel requests
 *
 * @param signals - Array of signals to combine (undefined values filtered out)
 * @returns Combined signal that aborts when any input signal aborts
 */
function anySignal(signals: Array<AbortSignal | null | undefined>): AbortSignal {
  const active = signals.filter((s): s is AbortSignal => s != null);
  if (active.length === 0) return new AbortController().signal;
  if (active.length === 1) return active[0];

  // Prefer native AbortSignal.any when available (modern browsers)
  const signalAny = (AbortSignal as unknown as { any?: (s: AbortSignal[]) => AbortSignal }).any;
  if (signalAny) return signalAny(active);

  // Fallback: create controller that aborts when any input aborts
  const controller = new AbortController();
  const onAbort = (): void => controller.abort();
  for (const s of active) {
    if (s.aborted) {
      controller.abort();
      break;
    }
    s.addEventListener('abort', onAbort, { once: true });
  }
  return controller.signal;
}

/**
 * Check if a status code should trigger a retry.
 */
function isRetryableStatus(status: number, retryableStatuses: number[]): boolean {
  return retryableStatuses.includes(status);
}

/**
 * Delay execution for the specified duration.
 */
function delay(ms: number): Promise<void> {
  return new Promise((resolve) => setTimeout(resolve, ms));
}

/**
 * Make a fetch request with automatic retry, exponential backoff, and timeout.
 *
 * P1-12: Add Retry Logic with Exponential Backoff
 *
 * Behavior:
 * - Retries on network errors and specified server errors (5xx by default)
 * - Does NOT retry on client errors (4xx) since those are permanent failures
 * - Uses exponential backoff: 1s, 2s, 4s, etc.
 * - Each request attempt has a configurable timeout (default 30s)
 * - Timeouts are NOT retried (considered a connection failure)
 *
 * @param url - The URL to fetch
 * @param init - Standard RequestInit options
 * @param options - Retry configuration options
 * @returns The fetch Response
 * @throws RequestTimeoutError if the request times out
 * @throws MaxRetriesExceededError if all retry attempts fail
 */
export async function fetchWithRetry(
  url: string,
  init: RequestInit = {},
  options: RetryOptions = {}
): Promise<Response> {
  const { maxRetries, baseDelayMs, retryableStatuses, timeoutMs, preserveFinalRetryableResponse } =
    {
      ...DEFAULT_OPTIONS,
      ...options,
    };

  let lastError: unknown;
  const totalAttempts = maxRetries + 1; // 1 initial + maxRetries

  for (let attempt = 0; attempt < totalAttempts; attempt++) {
    // Create AbortController for timeout handling (per-attempt)
    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), timeoutMs);

    try {
      // Combine consumer signal with timeout signal (review fix: signal override)
      const response = await fetch(url, {
        ...init,
        signal: anySignal([init.signal, controller.signal]),
      });

      // Success or non-retryable error (4xx client errors)
      if (response.ok || !isRetryableStatus(response.status, retryableStatuses)) {
        return response;
      }

      // A guarded, non-retried mutation may carry authoritative structured
      // recovery data (for example, which prefix committed) in a 5xx body.
      // Preserve only the FINAL response: an earlier failed attempt must never
      // mask a later network failure or be mistaken for the final outcome.
      if (preserveFinalRetryableResponse && attempt === totalAttempts - 1) {
        return response;
      }

      // Retryable server error - will retry if attempts remain
      lastError = new Error(`Server error: ${response.status} ${response.statusText}`);
    } catch (error) {
      // An AbortError can come from TWO sources merged into the fetch signal:
      // the caller's `init.signal` (an intentional cancel — e.g. a React
      // AbortController cleaning up a superseded request) or the internal
      // per-attempt timeout controller. These must NOT be conflated:
      //  - A caller cancel must propagate as a real AbortError so the caller can
      //    detect `err.name === 'AbortError'` and silently ignore it (not surface
      //    a spurious "request timed out" to the user).
      //  - A timeout maps to RequestTimeoutError (thrown immediately, no retry).
      if (error instanceof Error && error.name === 'AbortError') {
        if (init.signal?.aborted) {
          // Caller-initiated cancel — rethrow the real AbortError.
          throw error;
        }
        // Internal per-attempt timeout fired.
        throw new RequestTimeoutError();
      }

      // Network error (DNS, CORS, etc.) - will retry
      lastError = error;
    } finally {
      clearTimeout(timeoutId);
    }

    // Don't delay after the last attempt
    if (attempt < totalAttempts - 1) {
      const delayMs = baseDelayMs * Math.pow(2, attempt);
      await delay(delayMs);
    }
  }

  throw new MaxRetriesExceededError(totalAttempts, lastError);
}
